/**
 * PAPPO — ModerationNotice
 * Mostra ao usuário, no próximo acesso, as advertências da moderação ainda não
 * vistas. Ao confirmar ("Entendi"), reconhece todas (some do aviso). Montado no
 * shell autenticado, aparece em qualquer página logo após o login.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { listMyWarnings, ackWarning, type MyWarning } from '../../services/moderation';
import styles from './ModerationNotice.module.css';

function fmt(s: string): string {
  return new Date(s).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function ModerationNotice() {
  const [warnings, setWarnings] = useState<MyWarning[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      const r = await listMyWarnings();
      if (alive && r.success && r.data && r.data.warnings.length > 0) {
        setWarnings(r.data.warnings);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const dismiss = async () => {
    if (busy) return;
    setBusy(true);
    await Promise.all(warnings.map((w) => ackWarning(w.id)));
    setWarnings([]);
    setBusy(false);
  };

  if (warnings.length === 0) return null;

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true">
      <div className={styles.card}>
        <div className={styles.iconWrap}>
          <AlertTriangle size={40} />
        </div>
        <h2 className={styles.title}>
          {warnings.length > 1 ? 'Advertências da moderação' : 'Advertência da moderação'}
        </h2>
        <p className={styles.sub}>
          A equipe do PAPPO registrou {warnings.length > 1 ? 'estas advertências' : 'esta advertência'} na sua conta.
          Repetir a conduta pode levar a silenciamento, suspensão ou banimento.
        </p>
        <ul className={styles.list}>
          {warnings.map((w) => (
            <li key={w.id} className={styles.item}>
              <div className={styles.itemReason}>{w.reason || 'Conduta inadequada.'}</div>
              <div className={styles.itemMeta}>
                {w.issued_name ? `${w.issued_name} · ` : ''}
                {fmt(w.created_at)}
              </div>
            </li>
          ))}
        </ul>
        <button type="button" className={styles.btn} onClick={dismiss} disabled={busy}>
          {busy ? 'Confirmando…' : 'Entendi'}
        </button>
      </div>
    </div>
  );
}
